import React, { ReactElement, useCallback, useMemo, useRef, useState } from 'react'
import { Collapse, Overlay } from 'react-bootstrap'
import { NavLink } from 'react-router-dom'

import buttonStyles from '../../../components/Button/Button.module.scss'
import { useOutsideClick } from '../../../common/useOutsideClick'
import { getIntegrationDisplayName, IntegrationServiceId } from '../../../common/integrations'

import './AIAgentResultCitations.scss'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { AIAgentType } from '../../../models/AIAgent'
import { IntegrationIcon } from '../../../components/IntegrationIcon'

enum CitationSourceType {
  CRM = 'crm',
  CALENDAR = 'calendar',
  MAIL = 'mail',
  MEETING = 'meeting',
  PLANNING = 'planning',
  PORTFOLIO = 'portfolio',
  TAX = 'tax',
}

interface CRMCitationSource {
  type: CitationSourceType.CRM
  service_id: string
}

interface CalendarCitationSource {
  type: CitationSourceType.CALENDAR
  service_id: string
}

interface EmailCitationSource {
  type: CitationSourceType.MAIL
  service_id: string
}

interface MeetingCitationSource {
  type: CitationSourceType.MEETING
  room_id: string
  session_id: string
}

interface PlanningCitationSource {
  type: CitationSourceType.PLANNING
  service_id: string
}

interface PortfolioCitationSource {
  type: CitationSourceType.PORTFOLIO
  service_id: string
}

interface TaxCitationSource {
  type: CitationSourceType.TAX
  service_id: string
}

export type CitationSource =
  | CRMCitationSource
  | CalendarCitationSource
  | EmailCitationSource
  | MeetingCitationSource
  | PlanningCitationSource
  | PortfolioCitationSource
  | TaxCitationSource

export interface Citation {
  id: string
  description: string
  source: CitationSource
}

interface CitationSourceOption {
  icon: ReactElement
  label: string
  link?: string
}

const sourceIconClassName = 'ai-agent-result-citation-icon d-flex align-items-center justify-content-center zocks-p-0.125 rounded-1 zocks-bg-color-gray-100'

const getCitationSourceOption = (serviceId: IntegrationServiceId): CitationSourceOption => ({
  icon: (
    <div className={sourceIconClassName}>
      <IntegrationIcon serviceId={serviceId} />
    </div>
  ),
  label: getIntegrationDisplayName(serviceId),
})

const unknownCitationSourceOptions: CitationSourceOption = {
  icon: (
    <div className={`${sourceIconClassName} zocks-bg-color-gray-900`}>
      <i className='bi bi-question lead-large zocks-text-white d-flex align-items-center justify-content-center' />
    </div>
  ),
  label: 'Unknown',
}

const getCitationSourceOptions = (source: CitationSource): CitationSourceOption => {
  const isIntegrationCitation =
    source.type === CitationSourceType.CRM ||
    source.type === CitationSourceType.CALENDAR ||
    source.type === CitationSourceType.MAIL ||
    source.type === CitationSourceType.PLANNING ||
    source.type === CitationSourceType.PORTFOLIO ||
    source.type === CitationSourceType.TAX

  if (isIntegrationCitation && source.service_id) {
    return getCitationSourceOption(source.service_id as IntegrationServiceId) ?? unknownCitationSourceOptions
  }
  if (source.type === CitationSourceType.MEETING && source.room_id && source.session_id) {
    return {
      icon: (
        <div className={`${sourceIconClassName} zocks-bg-color-gray-900`}>
          <i className='icon-zocks-logo lead-large zocks-text-white d-flex align-items-center justify-content-center' />
        </div>
      ),
      label: 'Meeting',
      link: `/workspace/past-meetings/room/${source.room_id}/session/${source.session_id}`,
    }
  }
  return unknownCitationSourceOptions
}

interface AIAgentResultCitationsProps {
  citations: Citation[]
  citationIsLoading: boolean
  aiAgentType: AIAgentType
}

export const AIAgentResultCitations: React.FC<AIAgentResultCitationsProps> = ({ aiAgentType, citations, citationIsLoading }) => {
  const [isReferencesCollapsed, setIsReferencesCollapsed] = useState<boolean>(false)

  if (!citationIsLoading && !citations.length) {
    return null
  }

  return (
    <div className='border zocks-border-0.5 bg-white py-3 ignore-in-pdf'>
      <div className='d-flex justify-content-between align-items-center py-2 zocks-px-1.75' onClick={() => setIsReferencesCollapsed(!isReferencesCollapsed)}>
        <span className='lead-small'>References</span>
        <span className='paragraph-small text-decoration-underline'>Show {isReferencesCollapsed ? 'more' : 'less'}</span>
      </div>
      <Collapse in={!isReferencesCollapsed} dimension={'height'}>
        <div>
          {citationIsLoading ? (
            <div className='zocks-px-1.25 zocks-pb-1.25'>
              <div className='skeleton' style={{ width: '100%', height: '70px' }} />
            </div>
          ) : (
            <div className='d-flex flex-column align-items-start zocks-px-1.25 zocks-pb-1.25'>
              {citations.length ? (
                citations.map((citation, index) => <AIAgentResultCitationItem aiAgentType={aiAgentType} citation={citation} index={index} key={citation.id} />)
              ) : (
                <span className='paragraph-small'>No references could be generated.</span>
              )}
            </div>
          )}
        </div>
      </Collapse>
    </div>
  )
}

interface AIAgentResultCitationItemProps {
  citation: Citation
  index: number
  aiAgentType: AIAgentType
}

const AIAgentResultCitationItem: React.FC<AIAgentResultCitationItemProps> = ({ aiAgentType, citation, index }) => {
  const { tracked } = useTracking()

  const [show, setShow] = useState<boolean>(false)
  const ref = useRef<HTMLDivElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)

  const hideTooltip = useCallback(() => setShow(false), [])
  const citationsToDisplay = useMemo(() => {
    return citation ? [citation] : []
  }, [citation])

  useOutsideClick(tooltipRef, hideTooltip)

  const _handleOnClick = () => {
    if (!citation) {
      return
    }
    setShow((v) => !v)
  }

  const handleOnClick = tracked(_handleOnClick, {
    eventName: TrackEvent.CITATION_OPENED,
    eventDetails: { aiAgentType },
  })

  const { icon, label } = getCitationSourceOptions(citation.source)

  return (
    <>
      <div ref={ref} className='d-flex align-items-center gap-2 ai-agent-result-citation-item p-2 rounded-1' onClick={handleOnClick}>
        <span className='lead-small'>{index + 1}.</span>
        <div className='border-start border-1 zocks-border-color-black d-flex gap-2 align-items-center ps-3'>
          {icon}
          <span className='paragraph-small bold'>{label}</span>
        </div>
      </div>
      <AIAgentResultCitationTooltip citations={citationsToDisplay} index={index} show={show} target={ref.current} tooltipRef={tooltipRef} />
    </>
  )
}

export interface AIAgentResultCitationTooltipProps {
  citations: Citation[]
  index?: number
  show: boolean
  target: HTMLElement | null
  tooltipRef: React.RefObject<HTMLDivElement>
}

export interface CitationTooltipProps {
  show: boolean
  target: HTMLElement | null
  children: React.ReactNode
}

const CitationTooltip: React.FC<CitationTooltipProps> = ({ children, target, show }) => {
  return (
    <Overlay target={target} show={show} placement='top' offset={[0, 10]}>
      {({ ...props }) => (
        <div {...props} className='citation-tooltip zocks-border-0.25 p-4 ignore-in-pdf'>
          {children}
        </div>
      )}
    </Overlay>
  )
}

const SourceLink: React.FC<{ label: string; link?: string }> = ({ label, link }) =>
  link ? (
    <NavLink className={`${buttonStyles.button} ${buttonStyles.linkButton} button-link-small`} to={link} target='_blank'>
      <span className='paragraph-small bold'>{label}</span>
    </NavLink>
  ) : (
    <span className='paragraph-small bold'>{label}</span>
  )

export const AIAgentResultCitationTooltip: React.FC<AIAgentResultCitationTooltipProps> = ({ citations, index, show, target, tooltipRef }) => {
  return (
    <CitationTooltip target={target} show={show}>
      <div ref={tooltipRef} className='d-flex flex-column gap-4 align-items-start'>
        {index !== undefined && (
          <div className='d-flex align-items-center gap-3 mb-2'>
            <span className='rounded-1 zocks-bg-color-gray-900 zocks-text-white lead-extra-small citation-tooltip-index d-flex align-items-center justify-content-center'>
              {index + 1}
            </span>
            <span className='paragraph-medium bold'>
              This information can be found in the following source
              {citations.length > 1 ? 's:' : ':'}
            </span>
          </div>
        )}

        {citations.map((c, i) => {
          const { icon, label, link } = getCitationSourceOptions(c.source)
          return (
            <div key={i} className='border-start border-3 zocks-border-color-black px-3 py-2 zocks-bg-color-gray-100 w-100 d-flex flex-column gap-2'>
              <span className='paragraph-medium'>{c.description}</span>
              <div className='d-flex align-items-center gap-1'>
                <div className='me-2'>{icon}</div>
                <span className='paragraph-small'>This information was given in</span>
                <SourceLink label={label} link={link} />
              </div>
            </div>
          )
        })}
      </div>
    </CitationTooltip>
  )
}
